import React, { useState, useEffect, useRef } from "react";
import axios from "axios";
import { toast } from "react-toastify";

const UserDailyItems = ({ user }) => {
  const [dailyItems, setDailyItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [filterStatus, setFilterStatus] = useState("");
  const [filterBusiness, setFilterBusiness] = useState("");
  const [lastUpdated, setLastUpdated] = useState(null);
  const intervalRef = useRef(null);

  useEffect(() => {
    fetchUserDailyItems();

    // Set up polling to check for updates every 30 seconds
    intervalRef.current = setInterval(() => {
      fetchUserDailyItems(true); // Silent refresh
    }, 30000);

    // Add window focus listener for manual refresh when user comes back
    const handleFocus = () => {
      fetchUserDailyItems(true);
    };

    window.addEventListener("focus", handleFocus);

    // Cleanup interval and listener on unmount
    return () => {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
      }
      window.removeEventListener("focus", handleFocus);
    };
  }, [user]);

  const fetchUserDailyItems = async (silent = false) => {
    if (!silent) {
      setLoading(true);
      setError("");
    }

    try {
      const token = localStorage.getItem("token");
      // Use the main user daily items endpoint which properly filters by approved status
      const response = await axios.get(
        `https://dailybackend-zvvt.onrender.com/api/daily-items/`,
        {
          headers: { Authorization: `Bearer ${token}` },
        }
      );

      const newItems = response.data;

      // Check if items were removed (only if we had items before)
      if (
        silent &&
        dailyItems.length > 0 &&
        newItems.length < dailyItems.length
      ) {
        const removedCount = dailyItems.length - newItems.length;
        toast.info(
          `${removedCount} daily item${removedCount > 1 ? "s" : ""} ${
            removedCount > 1 ? "have" : "has"
          } been removed by the business.`,
          {
            position: "top-right",
            autoClose: 4000,
          }
        );
      }

      setDailyItems(newItems);
      setLastUpdated(new Date());

      if (!silent && error) {
        setError(""); // Clear any previous errors on successful fetch
      }
    } catch (err) {
      console.error("Error fetching user daily items:", err);
      if (!silent) {
        setError(err.response?.data?.message || "Error fetching daily items");
      }
    } finally {
      if (!silent) {
        setLoading(false);
      }
    }
  };

  const getStatusColor = (isActive) => {
    return isActive ? "#28a745" : "#6c757d";
  };

  const getStatusIcon = (isActive) => {
    return isActive ? "✅" : "⏸️";
  };

  const getFrequencyDisplay = (frequency, weeklyDays, monthlyDate) => {
    switch (frequency) {
      case "daily":
        return "📅 Daily";
      case "weekly":
        return `📊 Weekly (${weeklyDays?.join(", ") || "Not specified"})`;
      case "monthly":
        return `📅 Monthly (Day ${monthlyDate || "Not specified"})`;
      default:
        return frequency;
    }
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat("en-IN", {
      style: "currency",
      currency: "INR",
    }).format(amount || 0);
  };

  const formatDate = (dateString) => {
    if (!dateString) return "No end date";
    return new Date(dateString).toLocaleDateString("en-IN", {
      day: "2-digit",
      month: "short",
      year: "numeric",
    });
  };

  // Filter daily items based on status and business
  const filteredItems = dailyItems.filter((item) => {
    const statusMatch =
      !filterStatus ||
      (filterStatus === "active" && item.isActive) ||
      (filterStatus === "inactive" && !item.isActive);

    const businessMatch =
      !filterBusiness ||
      (item.createdBy?.businessName &&
        item.createdBy.businessName
          .toLowerCase()
          .includes(filterBusiness.toLowerCase()));

    return statusMatch && businessMatch;
  });

  // Get unique businesses for filter dropdown
  const uniqueBusinesses = [
    ...new Set(
      dailyItems
        .map((item) => item.createdBy?.businessName)
        .filter((name) => name)
    ),
  ];

  if (loading) {
    return (
      <div className="card">
        <div style={{ textAlign: "center", padding: "40px" }}>
          <p>Loading your daily items...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="user-daily-items">
      <div className="card">
        <div style={{ marginBottom: "30px" }}>
          <div
            style={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              marginBottom: "20px",
              flexWrap: "wrap",
              gap: "10px",
            }}
          >
            <h2>My Daily Items</h2>
            <div
              style={{
                display: "flex",
                alignItems: "center",
                gap: "15px",
                flexWrap: "wrap",
              }}
            >
              <div style={{ fontSize: "14px", color: "#666" }}>
                Total: {filteredItems.length} items
              </div>
              {lastUpdated && (
                <span
                  style={{
                    fontSize: "12px",
                    color: "#666",
                    fontStyle: "italic",
                  }}
                >
                  Last updated: {lastUpdated.toLocaleTimeString()}
                </span>
              )}
              <button
                onClick={() => fetchUserDailyItems()}
                disabled={loading}
                style={{
                  padding: "8px 15px",
                  backgroundColor: "#007bff",
                  color: "white",
                  border: "none",
                  borderRadius: "5px",
                  cursor: loading ? "not-allowed" : "pointer",
                  fontSize: "14px",
                  display: "flex",
                  alignItems: "center",
                  gap: "5px",
                }}
              >
                {loading ? "🔄" : "🔄"} Refresh
              </button>
            </div>
          </div>

          {/* Filters */}
          <div
            style={{
              display: "flex",
              gap: "15px",
              alignItems: "center",
              flexWrap: "wrap",
              marginBottom: "20px",
              padding: "15px",
              backgroundColor: "#f8f9fa",
              borderRadius: "8px",
            }}
          >
            <div>
              <label style={{ marginRight: "5px", fontWeight: "500" }}>
                Status:
              </label>
              <select
                value={filterStatus}
                onChange={(e) => setFilterStatus(e.target.value)}
                style={{
                  padding: "5px 10px",
                  borderRadius: "4px",
                  border: "1px solid #ddd",
                }}
              >
                <option value="">All Status</option>
                <option value="active">Active</option>
                <option value="inactive">Inactive</option>
              </select>
            </div>
            <div>
              <label style={{ marginRight: "5px", fontWeight: "500" }}>
                Business:
              </label>
              <select
                value={filterBusiness}
                onChange={(e) => setFilterBusiness(e.target.value)}
                style={{
                  padding: "5px 10px",
                  borderRadius: "4px",
                  border: "1px solid #ddd",
                }}
              >
                <option value="">All Businesses</option>
                {uniqueBusinesses.map((business, index) => (
                  <option key={index} value={business}>
                    {business}
                  </option>
                ))}
              </select>
            </div>
            <button
              onClick={() => {
                setFilterStatus("");
                setFilterBusiness("");
              }}
              style={{
                padding: "5px 15px",
                fontSize: "12px",
                border: "1px solid #6c757d",
                borderRadius: "4px",
                backgroundColor: "white",
                color: "#6c757d",
                cursor: "pointer",
              }}
            >
              Clear Filters
            </button>
          </div>
        </div>

        {error && (
          <div
            style={{
              backgroundColor: "#f8d7da",
              color: "#721c24",
              padding: "10px",
              borderRadius: "4px",
              marginBottom: "20px",
            }}
          >
            {error}
          </div>
        )}

        {filteredItems.length === 0 ? (
          <div
            style={{
              textAlign: "center",
              padding: "40px",
              backgroundColor: "#f8f9fa",
              borderRadius: "8px",
            }}
          >
            <h3>No Daily Items Found</h3>
            {dailyItems.length === 0 ? (
              <p>You don't have any daily items assigned to you yet.</p>
            ) : (
              <p>No items match your current filter criteria.</p>
            )}
          </div>
        ) : (
          <div className="daily-items-list">
            {filteredItems.map((item) => (
              <div
                key={item._id}
                className="daily-item-card"
                style={{
                  border: "1px solid #ddd",
                  borderRadius: "12px",
                  padding: "20px",
                  marginBottom: "15px",
                  backgroundColor: "white",
                  boxShadow: "0 2px 4px rgba(0,0,0,0.1)",
                }}
              >
                <div
                  style={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "flex-start",
                  }}
                >
                  {/* Main Content */}
                  <div style={{ flex: 1 }}>
                    <div
                      style={{
                        display: "flex",
                        alignItems: "center",
                        marginBottom: "12px",
                      }}
                    >
                      <h4 style={{ margin: 0, fontSize: "18px" }}>
                        {item.title}
                      </h4>
                      <div
                        style={{
                          display: "flex",
                          alignItems: "center",
                          gap: "5px",
                          marginLeft: "15px",
                          padding: "4px 8px",
                          borderRadius: "12px",
                          backgroundColor: `${getStatusColor(item.isActive)}15`,
                          border: `1px solid ${getStatusColor(
                            item.isActive
                          )}30`,
                        }}
                      >
                        <span>{getStatusIcon(item.isActive)}</span>
                        <span
                          style={{
                            color: getStatusColor(item.isActive),
                            fontSize: "12px",
                            fontWeight: "600",
                          }}
                        >
                          {item.isActive ? "ACTIVE" : "INACTIVE"}
                        </span>
                      </div>
                    </div>

                    {item.description && (
                      <p
                        style={{
                          margin: "0 0 12px 0",
                          color: "#666",
                          fontSize: "14px",
                        }}
                      >
                        {item.description}
                      </p>
                    )}

                    {/* Business Information */}
                    <div
                      style={{
                        backgroundColor: "#e3f2fd",
                        padding: "10px 12px",
                        borderRadius: "8px",
                        marginBottom: "12px",
                        border: "1px solid #bbdefb",
                      }}
                    >
                      <div
                        style={{
                          display: "flex",
                          alignItems: "center",
                          gap: "8px",
                        }}
                      >
                        <span style={{ fontSize: "16px" }}>🏢</span>
                        <strong style={{ color: "#1976d2" }}>
                          Created by Business:
                        </strong>
                        <span style={{ fontWeight: "600", color: "#1976d2" }}>
                          {item.createdBy?.businessName ||
                            item.createdBy?.username ||
                            "Unknown Business"}
                        </span>
                      </div>
                      {item.createdBy?.email && (
                        <div
                          style={{
                            marginTop: "4px",
                            fontSize: "12px",
                            color: "#1976d2",
                          }}
                        >
                          📧 {item.createdBy.email}
                        </div>
                      )}
                    </div>

                    {/* Item Details */}
                    <div
                      style={{
                        display: "grid",
                        gridTemplateColumns:
                          "repeat(auto-fit, minmax(200px, 1fr))",
                        gap: "15px",
                        marginBottom: "12px",
                      }}
                    >
                      <div>
                        <strong>📦 Category:</strong>
                        <span
                          style={{
                            marginLeft: "8px",
                            textTransform: "capitalize",
                          }}
                        >
                          {item.category}
                        </span>
                      </div>
                      <div>
                        <strong>🔢 Quantity:</strong>
                        <span style={{ marginLeft: "8px" }}>
                          {item.quantity} (Schedule: {item.scheduleQuantity})
                        </span>
                      </div>
                      <div>
                        <strong>💰 Price:</strong>
                        <span style={{ marginLeft: "8px" }}>
                          {formatCurrency(item.price)}
                        </span>
                      </div>
                      <div>
                        <strong>🕒 Delivery Time:</strong>
                        <span style={{ marginLeft: "8px" }}>
                          {item.deliveryTime}
                        </span>
                      </div>
                    </div>

                    {/* Schedule Information */}
                    <div
                      style={{
                        display: "grid",
                        gridTemplateColumns:
                          "repeat(auto-fit, minmax(200px, 1fr))",
                        gap: "15px",
                        fontSize: "14px",
                      }}
                    >
                      <div>
                        <strong>📅 Start Date:</strong>
                        <span style={{ marginLeft: "8px" }}>
                          {formatDate(item.startDate)}
                        </span>
                      </div>
                      <div>
                        <strong>🏁 End Date:</strong>
                        <span style={{ marginLeft: "8px" }}>
                          {item.isIndefinite
                            ? "Indefinite"
                            : formatDate(item.endDate)}
                        </span>
                      </div>
                      <div>
                        <strong>🔄 Frequency:</strong>
                        <span style={{ marginLeft: "8px" }}>
                          {getFrequencyDisplay(
                            item.frequency,
                            item.weeklyDays,
                            item.monthlyDate
                          )}
                        </span>
                      </div>
                      <div>
                        <strong>📅 Created:</strong>
                        <span style={{ marginLeft: "8px" }}>
                          {formatDate(item.createdAt)}
                        </span>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default UserDailyItems;
